import { prefersReducedMotion } from '@theme/utilities';

const ROOT_MARGIN = '20% 0px 20% 0px';
const THRESHOLDS = [0, 0.01, 0.1, 0.25, 0.5, 1];
const MAX_CONCURRENT_LOADS = 2;

/** @type {Set<HTMLVideoElement>} */
const loadingVideos = new Set();

/** @type {HTMLVideoElement[]} */
const loadQueue = [];

/** @type {WeakSet<HTMLVideoElement>} */
const preparedVideos = new WeakSet();

/**
 * Move any existing source URLs to data-src so the browser cannot fetch early.
 * @param {HTMLVideoElement} video
 */
function detachSources(video) {
  video.querySelectorAll('source').forEach((source) => {
    if (!(source instanceof HTMLSourceElement)) return;
    const url = source.getAttribute('src') || source.dataset.src;
    if (!url) return;
    source.dataset.src = url;
    source.removeAttribute('src');
  });

  if (video.src && !video.dataset.src) {
    video.dataset.src = video.src;
    video.removeAttribute('src');
  }
}

/**
 * @param {HTMLVideoElement} video
 * @returns {boolean}
 */
function attachSources(video) {
  let attached = false;

  video.querySelectorAll('source').forEach((source) => {
    if (!(source instanceof HTMLSourceElement)) return;
    const url = source.dataset.src;
    if (!url || source.getAttribute('src')) return;
    source.src = url;
    attached = true;
  });

  if (video.dataset.src && !video.getAttribute('src')) {
    video.src = video.dataset.src;
    attached = true;
  }

  return attached;
}

/**
 * @param {HTMLVideoElement} video
 */
function prepareVideo(video) {
  if (preparedVideos.has(video)) return;
  preparedVideos.add(video);

  video.muted = true;
  video.defaultMuted = true;
  video.playsInline = true;
  video.preload = 'none';
  video.setAttribute('muted', '');
  video.setAttribute('playsinline', '');
  video.setAttribute('preload', 'none');
  if (video.hasAttribute('autoplay')) {
    video.dataset.lazyAutoplay = 'true';
    video.removeAttribute('autoplay');
  }

  detachSources(video);
}

/**
 * @param {HTMLVideoElement} video
 */
function onLoadProgress(video) {
  if (!loadingVideos.has(video)) return;
  if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA || video.error) {
    loadingVideos.delete(video);
    drainQueue();
  }
}

function drainQueue() {
  while (loadingVideos.size < MAX_CONCURRENT_LOADS && loadQueue.length > 0) {
    const next = loadQueue.shift();
    if (!next) continue;
    if (!next.isConnected) continue;
    if (next.dataset.lazyWanted !== 'true') continue;
    warmVideo(next);
  }
}

/**
 * @param {HTMLVideoElement} video
 */
function requestWarm(video) {
  video.dataset.lazyWanted = 'true';

  if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    warmVideo(video);
    return;
  }

  if (loadingVideos.has(video) || loadQueue.includes(video)) return;

  if (loadingVideos.size < MAX_CONCURRENT_LOADS) {
    warmVideo(video);
    return;
  }

  loadQueue.push(video);
}

/**
 * @param {HTMLVideoElement} video
 */
function warmVideo(video) {
  prepareVideo(video);
  const attached = attachSources(video);
  video.preload = 'auto';

  if (attached || video.readyState === HTMLMediaElement.HAVE_NOTHING) {
    loadingVideos.add(video);
    video.addEventListener('loadeddata', () => onLoadProgress(video), { once: true });
    video.addEventListener('canplay', () => onLoadProgress(video), { once: true });
    video.addEventListener('error', () => onLoadProgress(video), { once: true });
    try {
      video.load();
    } catch {
      loadingVideos.delete(video);
    }
  }
}

/**
 * @param {HTMLVideoElement} video
 */
function cooldownVideo(video) {
  video.dataset.lazyWanted = 'false';
  const index = loadQueue.indexOf(video);
  if (index >= 0) loadQueue.splice(index, 1);
  loadingVideos.delete(video);
  if (video.paused && video.preload !== 'none') {
    video.preload = 'none';
  }
}

/**
 * @param {HTMLVideoElement} video
 */
function playVideo(video) {
  if (prefersReducedMotion() || document.hidden) return;
  if (!video.paused && !video.ended) return;

  const start = () => {
    video.play().catch(() => {});
  };

  if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    start();
    return;
  }

  video.addEventListener('loadeddata', start, { once: true });
}

/**
 * @param {HTMLVideoElement} video
 */
function pauseVideo(video) {
  if (!video.paused) video.pause();
}

/**
 * @param {HTMLVideoElement} video
 */
function bindLazyVideo(video) {
  if (video.dataset.lazyBound === 'true') return;
  video.dataset.lazyBound = 'true';

  prepareVideo(video);

  const shouldAutoplay = video.dataset.lazyAutoplay === 'true';
  const managedExternally = video.hasAttribute('data-lazy-external');

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0) {
          requestWarm(video);
          if (shouldAutoplay && !managedExternally) playVideo(video);
        } else {
          pauseVideo(video);
          if (entry.intersectionRatio === 0) cooldownVideo(video);
        }
      });
    },
    { root: null, rootMargin: ROOT_MARGIN, threshold: THRESHOLDS }
  );

  observer.observe(video);
}

/**
 * Public helper for slideshow/hero controllers that already manage play/pause.
 * @param {HTMLVideoElement} video
 */
export function warmLazyVideo(video) {
  prepareVideo(video);
  requestWarm(video);
}

/**
 * @param {ParentNode} [root]
 */
export function initLazyVideos(root = document) {
  root.querySelectorAll('video[data-lazy-video]').forEach((video) => {
    if (video instanceof HTMLVideoElement) bindLazyVideo(video);
  });
}

initLazyVideos();

// Hydrate videos injected later (section morph, quick-add, infinite scroll).
if (typeof MutationObserver !== 'undefined') {
  const mo = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches?.('video[data-lazy-video]')) {
          bindLazyVideo(/** @type {HTMLVideoElement} */ (node));
        }
        node.querySelectorAll?.('video[data-lazy-video]').forEach((video) => {
          if (video instanceof HTMLVideoElement) bindLazyVideo(video);
        });
      });
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
}
